import React, { useEffect, useMemo, useState } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import {
  Alert,
  DataTable,
  LayoutContentWithHeader,
  LayoutContentWrapper,
  getHeaderDesktopButtons,
  parseQuery,
} from '@jjj/components';
import { useLocation, useNavigate } from 'react-router-dom';
import { palette } from 'styled-theme';
import { t } from '../../../../i18n';
import { RootState } from '../../../../redux/store';
import listActions from './actions';
import { getColumns } from './columns';
import { ILegalEntitiesListState } from './reducer.types';
import { LEGAL_ENTITIES_CREATE_PATH, legalEntityEditPath } from '../paths';

interface IProps {
  data: ILegalEntitiesListState;
  canCreate: boolean;
  loadDataRequest: (params: Record<string, unknown>) => void;
  clearStore: () => void;
}

const LegalEntitiesList: React.FC<IProps> = ({ data, canCreate, loadDataRequest, clearStore }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { records, loading, filteredTotal } = data;
  // Records survive in Redux between mounts. Gate render until this mount has
  // dispatched its own clearStore+fetch so we never paint stale data first.
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const query = parseQuery(location.search ?? '');
    clearStore();
    loadDataRequest(query);
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (params: Record<string, unknown>) => {
    loadDataRequest(params);
  };

  const handleEdit = (id: string) => {
    navigate(legalEntityEditPath(id));
  };

  const handleCreate = () => {
    navigate(LEGAL_ENTITIES_CREATE_PATH);
  };

  const columns = useMemo(() => getColumns({ onEdit: handleEdit }), []);

  const headerActions = canCreate
    ? [
        {
          key: 'add',
          type: 'primary',
          dataCy: 'legal-entities-list-create-button',
          name: t('dash.admin.legalEntities.create_button_label'),
          onActionClick: handleCreate,
        },
      ]
    : [];

  const query = parseQuery(location.search ?? '');

  return (
    <LayoutContentWrapper>
      <Helmet defer={false}>
        <title>{t('dash.admin.legalEntities.list_title')}</title>
      </Helmet>
      <Alert
        type="custom"
        styleSettings={{
          backgroundColor: palette('alert', 0),
          iconClass: 'fas fa-info-circle',
          iconColor: palette('grayscale', 5),
          textColor: palette('grayscale', 5),
          closeIconColor: palette('grayscale', 5),
          closeButtonOpacity: 1,
        }}
        closable
        style={{ marginBottom: 16 }}
        data-cy="legal-entities-list-banner-dismiss"
      >
        {t('dash.admin.legalEntities.banner_message')}
      </Alert>
      <LayoutContentWithHeader
        header={t('dash.admin.legalEntities.list_title')}
        icon="gavel"
        actionBtns={getHeaderDesktopButtons(headerActions)}
        actionBtnsMobile={headerActions}
      >
        <DataTable
          columns={columns}
          dataSource={initialized ? records ?? [] : []}
          filter={query.filter}
          loadData={handleChange}
          loading={loading || !initialized}
          onChange={handleChange}
          total={filteredTotal}
          isStatefullUrl
          hideSearchbar
        />
      </LayoutContentWithHeader>
    </LayoutContentWrapper>
  );
};

const mapStateToProps = (state: RootState) => ({
  data: state.LegalEntitiesList,
  canCreate: Boolean(state.Permissions.data?.legal_entity?.create),
});

const mapDispatchToProps = (dispatch: Dispatch<RootState>) => ({
  loadDataRequest: (params: Record<string, unknown>) => dispatch(listActions.loadDataRequest(params)),
  clearStore: () => dispatch(listActions.clearStore()),
});

export default connect(mapStateToProps, mapDispatchToProps)(LegalEntitiesList);
